import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import * as echarts from 'echarts';

@Injectable({ providedIn: 'root' })
export class ChartExportService {

  exportPNG(chart: any, filename = 'chart'): void {
    const url = this.captureFullLegend(chart, 'canvas', { type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
    this.downloadDataURL(url, `${filename}.png`);
  }

  exportJPEG(chart: any, filename = 'chart'): void {
    const url = this.captureFullLegend(chart, 'canvas', { type: 'jpeg', pixelRatio: 2, backgroundColor: '#fff' });
    this.downloadDataURL(url, `${filename}.jpeg`);
  }

  exportSVG(chart: any, filename = 'chart'): void {
    // The live chart renders on canvas (for interactive performance), and ECharts'
    // getDataURL() only produces real SVG when the chart's own renderer is 'svg' - on a
    // canvas-rendered chart it silently falls back to a PNG data URL, which chart-export
    // then saved with a `.svg` extension (a mislabeled PNG, not valid SVG/XML). captureFullLegend
    // renders a throwaway instance with the same option/size instead, purely for this export.
    const url = this.captureFullLegend(chart, 'svg', { type: 'svg', backgroundColor: '#fff' });
    this.downloadDataURL(url, `${filename}.svg`);
  }

  exportPDF(chart: any, filename = 'chart'): void {
    const imgUrl = this.captureFullLegend(chart, 'canvas', { type: 'png', pixelRatio: 2, backgroundColor: '#fff' });

    // Use existing jsPDF from assets/js-libs
    const jsPDF = (window as any).jsPDF;
    if (jsPDF) {
      const img = new Image();
      img.onload = () => {
        const pdf = new jsPDF('landscape');
        const width = pdf.internal.pageSize.getWidth();
        const height = (img.height * width) / img.width;
        pdf.addImage(imgUrl, 'PNG', 0, 0, width, height);
        pdf.save(`${filename}.pdf`);
      };
      img.src = imgUrl;
    } else {
      // Fallback: download as PNG if jsPDF not available
      this.exportPNG(chart, filename);
    }
  }

  viewFullscreen(element: HTMLElement): void {
    if (element.requestFullscreen) {
      element.requestFullscreen();
    }
  }

  exitFullscreen(): void {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  }

  printChart(chart: any): void {
    const url = this.captureFullLegend(chart, 'canvas', { type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
    const win = window.open('');
    if (win) {
      // The popup is a blank document - none of the app's stylesheets (including the
      // Bootstrap @page rules) load into it, so the print layout must be self-contained
      // here. Without explicit sizing the image prints at its natural pixel size (chart
      // size x pixelRatio 2), which overflows a portrait page's width (clipping the
      // chart) or a landscape page's height (splitting chart/legend across extra pages).
      // Scaling to the viewport with object-fit: contain keeps it on one page either way.
      win.document.write(`
        <html>
          <head>
            <style>
              @page { margin: 0.25in; }
              html, body {
                margin: 0;
                height: 100%;
              }
              body {
                display: flex;
                align-items: flex-start;
                justify-content: center;
              }
              img {
                max-width: 100%;
                max-height: 100vh;
                object-fit: contain;
                page-break-inside: avoid;
              }
            </style>
          </head>
          <body>
            <img src="${url}" onload="window.print();window.close()" />
          </body>
        </html>
      `);
      win.document.close();
    }
  }

  // getDataURL() only rasterizes what's currently on the canvas. The live legend normally
  // already runs type: 'plain' (wraps instead of paginating) with grid.bottom kept in sync with
  // however many rows it currently wraps to - in that case the live option already has correct,
  // full-legend layout baked in, and re-adding wrap-row height here would double it up (padding
  // the export with extra empty space below the chart). Only grow the canvas when the legend is
  // still paginated (type: 'scroll'), whose grid.bottom was sized for a single row and needs
  // extra room once switched to 'plain' so wrapped rows don't overlap the x-axis.
  private captureFullLegend(
    chart: any,
    renderer: 'canvas' | 'svg',
    dataURLOptions: { type: 'svg' | 'png' | 'jpeg'; pixelRatio?: number; backgroundColor: string }
  ): string {
    const option = chart.getOption();
    const width = chart.getWidth();
    const height = chart.getHeight();

    const liveLegend = Array.isArray(option.legend) ? option.legend[0] : option.legend;
    const legend = { ...liveLegend, type: 'plain' };
    const grid = { ...(Array.isArray(option.grid) ? option.grid[0] : option.grid) };

    let extraHeight = 0;
    if (liveLegend?.type !== 'plain') {
      const seriesNames: string[] = (option.series || []).map((s: any) => s.name).filter(Boolean);
      const rows = this.countLegendRows(seriesNames, width, legend.textStyle?.fontSize || 12);
      extraHeight = (rows - 1) * 20;
      grid.bottom = (typeof grid.bottom === 'number' ? grid.bottom : 40) + extraHeight;
    }

    const offscreen = echarts.init(document.createElement('div'), null, {
      renderer,
      width,
      height: height + extraHeight
    });
    offscreen.setOption({ ...option, legend, grid });
    const url = offscreen.getDataURL(dataURLOptions);
    offscreen.dispose();
    return url;
  }

  // Mirrors ECharts' own left-to-right legend packing closely enough to size the export canvas:
  // walk items in order, wrapping to a new row whenever the running row width would exceed the
  // container.
  private countLegendRows(names: string[], containerWidth: number, fontSize: number): number {
    const ITEM_ICON_WIDTH = 25;
    const ITEM_GAP = 10;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.font = `${fontSize}px sans-serif`;
    let rows = 1;
    let rowWidth = 0;
    for (const name of names) {
      const textWidth = ctx ? ctx.measureText(name).width : name.length * fontSize * 0.6;
      const itemWidth = ITEM_ICON_WIDTH + textWidth + ITEM_GAP;
      if (rowWidth > 0 && rowWidth + itemWidth > containerWidth) {
        rows++;
        rowWidth = itemWidth;
      } else {
        rowWidth += itemWidth;
      }
    }
    return rows;
  }

  private downloadDataURL(dataURL: string, filename: string): void {
    const commaIndex = dataURL.indexOf(',');
    const header = dataURL.slice(0, commaIndex);
    const payload = dataURL.slice(commaIndex + 1);

    // Parsed with indexOf/slice rather than a regex (e.g. /:(.*?);/) - SonarQube flags that
    // pattern as vulnerable to superlinear backtracking on crafted input; this is O(n) with no
    // backtracking risk.
    const colonIndex = header.indexOf(':');
    const semicolonIndex = header.indexOf(';', colonIndex + 1);
    const mime =
      colonIndex >= 0 && semicolonIndex > colonIndex
        ? header.slice(colonIndex + 1, semicolonIndex)
        : 'application/octet-stream';

    // ECharts' canvas-based exports (png/jpeg) return base64 data URLs, but its SVG renderer
    // (zrender SVGPainter.toDataURL) returns a URI-encoded data URL instead - there is no
    // base64 variant for it. atob() on that payload throws InvalidCharacterError, so decode
    // according to which encoding is actually present rather than assuming base64.
    let blob: Blob;
    if (header.endsWith(';base64')) {
      const bstr = atob(payload);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      blob = new Blob([u8arr], { type: mime });
    } else {
      blob = new Blob([decodeURIComponent(payload)], { type: mime });
    }
    this.saveAs(blob, filename);
  }

  // Thin wrapper around the file-saver module so tests can spy on it. Strict ESM under esbuild
  // forbids mutating the module-level `saveAs` binding (spyOn(FileSaver, 'saveAs') fails with
  // "not declared writable"); a method on the service is a writable property.
  protected saveAs(blob: Blob, filename: string): void {
    saveAs(blob, filename);
  }
}
